import Figure from "@hydrologiq/shared/figures/generic/Figure"
import { roundToDP } from "@hydrologiq/common/helpers/math"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import TimeToDepletion from "@hydrologiq/shared/figures/generic/TimeToDepletion"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import TrendIndicator from "@hydrologiq/shared/figures/generic/TrendIndicator"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"

interface CustomersHeadlinesProps {
  totalOrdered: number
  totalConsumed: number
  averageOrder: number
  returnedPerFulfillment: number
  returnedTrend: number
  earliestRunDryMinutes?: number
  earliestRunDrySite?: { id: string; name: string }
  earliestRunDryCustomer?: { id: string; name: string }
  loading?: boolean
  error?: string | null
}

function CustomersHeadlines({
  totalOrdered,
  totalConsumed,
  averageOrder,
  returnedPerFulfillment,
  returnedTrend,
  earliestRunDryMinutes,
  earliestRunDrySite,
  earliestRunDryCustomer,
  loading = false,
  error = null,
}: CustomersHeadlinesProps) {
  const assetDepletionEnabled = useGetFeature("asset_depletion")
  const rounded = {
    totalOrdered: roundToDP(totalOrdered, 2),
    totalConsumed: roundToDP(totalConsumed, 2),
    averageOrder: roundToDP(averageOrder, 2),
    returnedPerFulfillment: roundToDP(returnedPerFulfillment, 2),
    returnedTrend: roundToDP(returnedTrend, 2),
  }

  const consumedPercentage =
    rounded.totalConsumed && rounded.totalOrdered ? Math.round((rounded.totalConsumed / rounded.totalOrdered) * 100) : 0

  return (
    <FigureGrid>
      {/* Asset Depleted in */}
      {assetDepletionEnabled && (
        <TimeToDepletion
          heading="Asset Depleted in"
          minutes={earliestRunDryMinutes}
          location={earliestRunDrySite?.name}
          customer={earliestRunDryCustomer?.id}
          site={earliestRunDrySite?.id}
          granularity="days"
          loading={loading}
          error={error ?? undefined}
          isPlaceholder={!earliestRunDryMinutes}
        />
      )}

      {/* Hydrogen Ordered */}
      <Figure heading="Hydrogen Ordered" error={error ?? undefined} loading={loading}>
        <FigureContent.Root>
          <FigureContent.SubText link={<TrendIndicator change={consumedPercentage} direction={consumedPercentage > 0 ? "decrease" : "neutral"} />}>all time</FigureContent.SubText>
          <FigureContent.Value>{rounded.totalOrdered} kg</FigureContent.Value>
        </FigureContent.Root>
      </Figure>

      {/* Average Order */}
      <Figure heading="Average Ordered" error={error ?? undefined} loading={loading}>
        <FigureContent.Root>
          <FigureContent.SubText>per order</FigureContent.SubText>
          <FigureContent.Value>{rounded.averageOrder} kg</FigureContent.Value>
        </FigureContent.Root>
      </Figure>

      {/* Returned Hydrogen per Fulfillment */}
      <Figure heading="Returned Hydrogen" error={error ?? undefined} loading={loading}>
        <FigureContent.Root>
          <FigureContent.SubText link={<TrendIndicator change={rounded.returnedTrend} />}>per fulfilment (avg)</FigureContent.SubText>
          <FigureContent.Value>{rounded.returnedPerFulfillment} kg</FigureContent.Value>
        </FigureContent.Root>
      </Figure>
    </FigureGrid>
  )
}

export default CustomersHeadlines
